import { IoCreateOutline, IoCopyOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import IconButton from "../../../../components/Buttons/IconButton";
import DefaultPagination from "../../../../components/Pagination";
import AgGridTable from "../../../../components/Tables/AgGrid";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { get, post, del } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { useAuth } from "../../../../context/auth";

function ActiveCellRenderer({ value, context }) {
  return value
    ? <Badge color="success">{context.t('common.active')}</Badge>
    : <Badge color="secondary">{context.t('common.inactive')}</Badge>;
}

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      {context.can('production.boms.create') && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.cloneBillOfMaterial(data.id);
          }}
          size="sm" outline color="secondary"
          title={context.t('common.clone')}
        >
          <IoCopyOutline />
        </IconButton>
      )}
      {context.can('production.boms.edit') && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.navigate('/production/bills-of-materials/edit/' + data.id, { state: { billOfMaterialData: data } });
          }}
          size="sm" outline color="secondary"
        >
          <IoCreateOutline />
        </IconButton>
      )}
      {context.can('production.boms.delete') && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.setBillOfMaterialToDelete(data.id);
          }}
          size="sm" outline color="danger"
        >
          <BsTrash3 />
        </IconButton>
      )}
    </div>
  );
}

function BillsOfMaterialsPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [billOfMaterialToDelete, setBillOfMaterialToDelete] = useState(null);

  const getBillsOfMaterials = useCallback(async () => {
    setLoading(true);
    const data = await get(`/production/bills-of-materials?page=${currentPage}&page_size=${pageSize}`);
    if(data?.success) {
      setRowData(data.bills_of_materials.data);
      setTotalPages(data.bills_of_materials.last_page);
      setTotalRecords(data.bills_of_materials.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getBillsOfMaterials();
  }, [getBillsOfMaterials]);

  const deleteBillOfMaterial = async () => {
    const data = await del('/production/bills-of-materials/delete/' + billOfMaterialToDelete);
    if(data?.success) {
      toast.success(t('production.billOfMaterialsDeleted'));
      getBillsOfMaterials();
    }
    setBillOfMaterialToDelete(null);
  };

  const cloneBillOfMaterial = useCallback(async (id) => {
    const data = await post('/production/bills-of-materials/clone/' + id);
    if(data?.success) {
      toast.success(t('production.billOfMaterialsCloned'));
      getBillsOfMaterials();
    }
  }, [t, getBillsOfMaterials]);

  const columnDefs = useMemo(() => [
    { field: 'reference', headerName: t('common.reference'), flex: 1, sortable: true },
    { field: 'name', headerName: t('common.name'), flex: 1, sortable: true },
    {
      field: 'product.name',
      headerName: t('production.finishedProduct'),
      flex: 2,
      sortable: true,
      valueGetter: (params) => params.data?.product?.name ?? '-'
    },
    { field: 'quantity', headerName: t('production.batchQuantity'), flex: 1, sortable: true },
    {
      headerName: t('production.components'),
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.data?.lines?.length ?? 0
    },
    { field: 'is_active', headerName: t('common.status'), flex: 1, sortable: true, cellRenderer: ActiveCellRenderer },
    {
      field: 'created_at',
      headerName: t('common.date'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value)
    },
    { headerName: '', width: 140, sortable: false, cellRenderer: ActionsCellRenderer }
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(
    () => ({ navigate, t, can, setBillOfMaterialToDelete, cloneBillOfMaterial }),
    [navigate, t, can, cloneBillOfMaterial]
  );

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sidebar.billsOfMaterials')} onReload={getBillsOfMaterials} dataTotal={totalRecords}>
          {can('production.boms.create') && (
            <DefaultButton color="primary" size="sm" onClick={() => navigate('/production/bills-of-materials/create')}>
              {t('production.newBillOfMaterials')}
            </DefaultButton>
          )}
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!billOfMaterialToDelete}
        toggle={() => setBillOfMaterialToDelete(null)}
        title={t('production.deleteBillOfMaterials')}
        message={t('production.deleteBillOfMaterialsConfirm')}
        onConfirm={deleteBillOfMaterial}
      />
    </SidebarLayout>
  );
}

export default BillsOfMaterialsPage;
